'use client';

import { Button } from '@/components/ui/button';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLegend,
  FieldSet,
  FieldTitle,
} from '@/components/ui/field';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  useActiveOrganization,
  useUpdateOrganization,
} from '@/features/organization';
import {
  updateOrganizationFields,
  updateOrganizationForm,
} from '@/features/organization/api/update-organization/update-organization.form';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

// Save only the appointment-reminder channels.
const F = updateOrganizationFields;

const remindersSchema = z.object({
  emailRemindersEnabled: F.emailRemindersEnabled.schema,
  smsRemindersEnabled: F.smsRemindersEnabled.schema,
});

type RemindersFormValues = z.infer<typeof remindersSchema>;

export function RemindersTab({
  className,
  onCancel,
  ...props
}: React.ComponentProps<'form'> & { onCancel?: () => void }) {
  const L = updateOrganizationForm.labels;
  const {
    data: organizationData,
    isPending: isOrganizationLoading,
    error: organizationError,
  } = useActiveOrganization();
  const { execute: updateOrganization, isExecuting: isUpdating } =
    useUpdateOrganization();

  const form = useForm<RemindersFormValues>({
    resolver: zodResolver(remindersSchema),
    defaultValues: {
      emailRemindersEnabled: organizationData?.emailRemindersEnabled ?? true,
      smsRemindersEnabled: organizationData?.smsRemindersEnabled ?? true,
    },
  });

  useEffect(() => {
    if (organizationData) {
      form.reset({
        emailRemindersEnabled: organizationData.emailRemindersEnabled ?? true,
        smsRemindersEnabled: organizationData.smsRemindersEnabled ?? true,
      });
    }
  }, [organizationData, form]);

  async function onSubmit(values: RemindersFormValues) {
    await updateOrganization({
      emailRemindersEnabled: values.emailRemindersEnabled,
      smsRemindersEnabled: values.smsRemindersEnabled,
    });
  }

  if (isOrganizationLoading) {
    return (
      <div className={cn('flex flex-col px-6 py-4 gap-6', className)}>
        <div className="animate-pulse space-y-6">
          <div className="h-12 bg-muted rounded" />
          <div className="h-12 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (organizationError) {
    return (
      <div className={cn('flex flex-col px-6 py-4 gap-6', className)}>
        <div className="text-destructive">
          Error loading organization data: {organizationError.message}
        </div>
      </div>
    );
  }

  return (
    <form
      className={cn('flex flex-col px-6 py-4', className)}
      onSubmit={form.handleSubmit(onSubmit)}
      {...props}
    >
      <FieldSet>
        <FieldLegend>Appointment reminders</FieldLegend>
        <FieldDescription className="-mt-2 mb-1">
          Customers are reminded 24 hours and 1 hour before their appointment.
          Choose how.
        </FieldDescription>

        <Controller
          name="emailRemindersEnabled"
          control={form.control}
          render={({ field }) => (
            <Field orientation="horizontal">
              <FieldContent>
                <FieldTitle>{L.emailRemindersEnabled}</FieldTitle>
                <FieldDescription>
                  Send the reminder to customers who have an email address.
                </FieldDescription>
              </FieldContent>
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
                aria-label={L.emailRemindersEnabled}
              />
            </Field>
          )}
        />

        <Controller
          name="smsRemindersEnabled"
          control={form.control}
          render={({ field }) => (
            <Field orientation="horizontal">
              <FieldContent>
                <FieldTitle>{L.smsRemindersEnabled}</FieldTitle>
                <FieldDescription>
                  Text the reminder to customers who have a phone number, from
                  your SMS number. Replies arrive in your inbox. Texts are not
                  sent before 8am or after 9pm.
                </FieldDescription>
              </FieldContent>
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
                aria-label={L.smsRemindersEnabled}
              />
            </Field>
          )}
        />
      </FieldSet>

      <Separator className="my-4" />

      <div className="flex gap-2">
        <Button type="submit" disabled={isUpdating}>
          {isUpdating ? 'Saving...' : 'Submit'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
